import React, { useMemo } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Input,
  Select,
  SelectOption,
  Text,
} from "@showitinc/showit-wc";
import HelpConstants from "../../../constants/HelpConstants";
import useBdowLogin from "../../../hooks/query/useBdowLogin";
import useBdowSites from "../../../hooks/query/useBdowSites";
import useOpenAccount, { PATH_THIRD_PARTY, WINDOW_NEW } from "../../../hooks/useOpenAccount";
import { initFormatter } from "../../../utils/PropertyFormatters";
import uiPrefix from "../../../utils/UiPrefixUtils";
import styles from "./SiteSettingsModal.module.css";

export const BDOW_SITE_ID_PROP = "config.bdowSiteID";
export const BDOW_SITE_SELECT = "bdow-site-select";
export const BDOW_OPEN_BUTTON = "bdow-open-button";
export const BDOW_MANAGE_CONNECTION_BUTTON = "bdow-manage-connection-button";
export const UNLISTED_SITE_MESSAGE =
  "This SiteID does not match your connected sites. Confirm this ID in your BDOW! account.";
export const OPEN_FAILED_MESSAGE = "Could not open BDOW! right now. Please try again.";
export const NO_CONNECTION_MESSAGE =
  "No BDOW! connection found. Connect your BDOW! account in your Showit account settings.";

const BDOW_SKIP_WP_PROP = "config.bdowSkipWP";

const BDOW = ({ siteSettings, onChange }) => {
  const bdowSiteID = siteSettings.getIn(BDOW_SITE_ID_PROP.split("."), "");
  const { bdowSites, isLoading: isLoadingSites } = useBdowSites({
    refetchOnWindowFocus: "always",
  });
  const { openBdow, isOpening, isError: isLoginErrorVisible, isConnectionMissing } = useBdowLogin();
  const [openAccountProfile] = useOpenAccount({ path: PATH_THIRD_PARTY, windowTarget: WINDOW_NEW });

  const { onChange: _onChange } = initFormatter((propInfo) => {
    const trimmedPropInfo = { ...propInfo, value: propInfo.value.trim() };
    onChange(trimmedPropInfo);
  });

  // Copied before sorting: bdowSites is the cached query array, and sort() mutates in place.
  const sortedBdowSites = useMemo(
    () =>
      [...bdowSites].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" }),
      ),
    [bdowSites],
  );

  const hasBdowSites = bdowSites.length > 0;
  const selectedSiteID = bdowSites.some((site) => site.id === bdowSiteID) ? bdowSiteID : "";
  const isUnlistedSiteID = hasBdowSites && !!bdowSiteID && !selectedSiteID;
  const onSiteIdChange = _onChange(BDOW_SITE_ID_PROP);

  const siteIdInputProps = {
    bordered: true,
    type: "text",
    propName: BDOW_SITE_ID_PROP,
    label: "SiteID",
    value: bdowSiteID,
    onSIInput: onSiteIdChange,
    onSIChange: onSiteIdChange,
    placeholder: "",
  };

  return (
    <Box mb="3">
      <Box p="3">
        <Text variant="heading-1" className={styles.codeTitle}>
          BDOW!
        </Text>
      </Box>
      <Box px="3" pb="3">
        <Text variant="small" variantStyle="light">
          BDOW! is the best pop-up tool for Showit users to collect emails or drive actions by
          making the right offer to the right visitor at the right time.&nbsp;
          <a href={HelpConstants.BDOW} target="_blank" rel="noreferrer">
            Learn More
          </a>
        </Text>
      </Box>
      {hasBdowSites ? (
        <>
          <Select
            data-testid={BDOW_SITE_SELECT}
            label="Connected Site"
            fullWidth
            value={selectedSiteID}
            onSISelect={_onChange(BDOW_SITE_ID_PROP, bdowSiteID)}
            minParent
            filter
          >
            <SelectOption value="">-- Select a connected site --</SelectOption>
            {sortedBdowSites.map((site) => (
              <SelectOption key={site.id} value={site.id}>
                {site.name}
              </SelectOption>
            ))}
          </Select>
          <Input {...siteIdInputProps} fullWidth />
          {isUnlistedSiteID && (
            <Box px="3" pt="1" pb="2">
              <Alert
                variant="info"
                stacked
                weight="bold"
                message={UNLISTED_SITE_MESSAGE}
              />
            </Box>
          )}
        </>
      ) : (
        <Box mb="2">
          <Input {...siteIdInputProps} className={styles["text-field"]} longLabel />
        </Box>
      )}
      <Box px="3" pt="2" pb="1">
        <Button
          data-testid={BDOW_OPEN_BUTTON}
          data-e2e={uiPrefix("bdow-open-btn")}
          variant="outline"
          size="md"
          loading={isOpening || isLoadingSites}
          onSIClick={() => openBdow(selectedSiteID || undefined)}
        >
          Open BDOW!
        </Button>
      </Box>
      {isLoginErrorVisible && (
        <Box px="3" pb="2">
          <Alert
            variant="error"
            stacked
            weight="bold"
            message={isConnectionMissing ? NO_CONNECTION_MESSAGE : OPEN_FAILED_MESSAGE}
          />
          {isConnectionMissing && (
            <Box pt="2">
              <Button
                data-testid={BDOW_MANAGE_CONNECTION_BUTTON}
                data-e2e={uiPrefix("bdow-manage-connection-btn")}
                variant="outline"
                size="sm"
                onSIClick={openAccountProfile}
              >
                Manage Connection
              </Button>
            </Box>
          )}
        </Box>
      )}
      <Box d="flex" mb="2">
        <Checkbox
          className={styles.checkbox}
          checked={siteSettings.getIn(BDOW_SKIP_WP_PROP.split("."), false)}
          label="Do not include on blog templates"
          onSIChange={(e) => onChange({ name: BDOW_SKIP_WP_PROP, value: e.target.checked })}
        />
      </Box>
    </Box>
  );
};

export default BDOW;
